import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button, Card, CardBody, Dialog, DialogBody, DialogFooter, DialogHeader, Spinner, Typography } from '@material-tailwind/react';
import ReactSelect from 'react-select';
import {
  FaArrowLeft,
  FaCalendarAlt,
  FaFileInvoiceDollar,
  FaLock,
  FaPlus,
  FaSave,
} from 'react-icons/fa';
import { useAuth } from '../AuthContext';
import { rootUrl } from '../../../config/config';
import OwnerTransactionPreviewDialog from './OwnerTransactionPreviewDialog';

import DesktopRow from './monthly-invoice-editor/DesktopRow';
import MobileRow from './monthly-invoice-editor/MobileRow';
import EmptyState from './monthly-invoice-editor/EmptyState';
import { DateInput, TextAreaInput } from './monthly-invoice-editor/Inputs';
import { toNumber, money, dateInput, displayDate, periodFromDate, formatPeriodLabel, makeRowFromCar, normalizeRow } from './monthly-invoice-editor/helpers';
import { formatMoney } from './formatMoney';

const statusMetaMap = {
  DRAFT: { label: 'Draft', badgeClass: 'bg-amber-100 text-amber-800 ring-1 ring-amber-200' },
  SENT: { label: 'Sent', badgeClass: 'bg-sky-100 text-sky-800 ring-1 ring-sky-200' },
  PAID: { label: 'Paid', badgeClass: 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200' },
  REVERSED: { label: 'Reversed', badgeClass: 'bg-rose-100 text-rose-700 ring-1 ring-rose-200' },
};

const buildRowDraft = (row, field, value, daysInMonth = 31) => {
  const next = { ...row, [field]: value };

  if (field === 'plate_no') next.plate_no = String(value || '').toUpperCase();
  if (field === 'allocation_date') next.allocation_date = dateInput(value);
  if (field === 'days_counted') next.days_counted = String(value);
  if (field === 'include') next.include = Boolean(value);

  if (field === 'base_amount') {
    next.base_amount = value === '' ? '' : money(value);
  }

  if (field === 'days_counted' || field === 'base_amount' || field === 'discount_amount') {
    const baseAmount = Math.max(0, toNumber(next.base_amount));
    const rawDays = String(next.days_counted ?? '').trim();
    const parsedDaysCounted = Math.max(0, parseInt(rawDays, 10) || 0);
    const daysCounted = rawDays === '' ? daysInMonth : parsedDaysCounted;
    const discount = Math.max(0, toNumber(next.discount_amount));
    const gross = daysInMonth > 0 ? (baseAmount * daysCounted) / daysInMonth : baseAmount;
    const normalizedGross = Math.max(0, gross);
    const normalizedDiscount = Math.max(0, Math.min(discount, normalizedGross));

    next.gross_amount = money(normalizedGross);
    next.discount_amount = money(normalizedDiscount);
    next.prorated_amount = money(Math.max(0, normalizedGross - normalizedDiscount));
  }

  return next;
};

  const buildRowErrors = (row, invoicePeriod, invoicePeriodLabel, rows) => {
  const errors = {};
  if (row.include === false) {
    return errors;
  }

  const plate = String(row.plate_no || '').trim().toUpperCase();
  const rowPeriod = row.invoice_period || row.detail_invoice_period || invoicePeriod;
  const gross = toNumber(row.gross_amount);
  const discount = toNumber(row.discount_amount);
  const hasContent = plate || row.allocation_date || gross > 0 || discount > 0 || toNumber(row.base_amount) > 0 || row.note || row.days_counted;

  if (hasContent && !plate) errors.plate_no = 'Plate is required.';
  if (discount > gross) errors.discount_amount = 'Discount cannot exceed gross.';
  if (String(row.days_counted || '').trim() !== '' && Number(row.days_counted) < 0) errors.days_counted = 'Days must be 0 or more.';
  if (plate) {
    const duplicateCount = rows.filter((candidate) => {
      if (candidate.include !== true) {
        return false;
      }
      const candidatePlate = String(candidate.plate_no || '').trim().toUpperCase();
      const candidatePeriod = candidate.invoice_period || candidate.detail_invoice_period || invoicePeriod;
      return candidatePlate === plate && candidatePeriod === rowPeriod;
    }).length;
    if (duplicateCount > 1) errors.plate_no = 'Duplicate plate number for the same period.';
  }

  return errors;
};

// DesktopRow, MobileRow and EmptyState have been moved to the monthly-invoice-editor folder

const MonthlyInvoiceEditor = () => {
  const { ownerCode, transId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const authToken = token || localStorage.getItem('authToken');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [ownerCars, setOwnerCars] = useState([]);
  const [defaultMonthlyAmount, setDefaultMonthlyAmount] = useState(0);
  const [ownerCarsLoading, setOwnerCarsLoading] = useState(false);
  const [transaction, setTransaction] = useState(null);
  const [invoiceDate, setInvoiceDate] = useState('');
  const [notes, setNotes] = useState('');
  const [rows, setRows] = useState([]);
  const [saveSuccess, setSaveSuccess] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedPlateOption, setSelectedPlateOption] = useState(null);
  const [saveValidation, setSaveValidation] = useState(null);
  const saveValidationRef = useRef(null);

  const invoiceStatus = useMemo(() => {
    if (transaction?.reversed === true || transaction?.reversed === 1 || transaction?.reversed === '1') return 'REVERSED';
    const raw = String(transaction?.invoice_status || '').trim().toUpperCase();
    return ['DRAFT', 'SENT', 'PAID'].includes(raw) ? raw : 'DRAFT';
  }, [transaction]);

  const isEditable = invoiceStatus === 'DRAFT';
  const statusMeta = statusMetaMap[invoiceStatus] || statusMetaMap.DRAFT;
  const invoicePeriod = useMemo(() => periodFromDate(transaction?.date), [transaction?.date]);
  const invoicePeriodLabel = useMemo(() => formatPeriodLabel(invoicePeriod), [invoicePeriod]);
  const invoiceDaysInMonth = useMemo(() => {
    if (!invoicePeriod) return 31;
    const [yearValue, monthValue] = invoicePeriod.split('-').map((value) => parseInt(value, 10));
    if (!yearValue || !monthValue) return 31;
    return new Date(yearValue, monthValue, 0).getDate();
  }, [invoicePeriod]);

  const dateBounds = useMemo(() => {
    if (!invoicePeriod) return { min: '', max: '' };
    const [yearValue, monthValue] = invoicePeriod.split('-').map((value) => parseInt(value, 10));
    return {
      min: `${invoicePeriod}-01`,
      max: `${invoicePeriod}-${String(new Date(yearValue, monthValue, 0).getDate()).padStart(2, '0')}`,
    };
  }, [invoicePeriod]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const ownerPlateOptions = useMemo(() => {
    const taken = new Set(
      rows
        .filter((row) => (row.invoice_period || row.detail_invoice_period || invoicePeriod) === invoicePeriod)
        .map((row) => String(row.plate_no || '').trim().toUpperCase())
        .filter(Boolean)
    );

    return ownerCars
      .map((car) => ({
        value: String(car.plate_no || '').trim().toUpperCase(),
        label: String(car.plate_no || '').trim().toUpperCase(),
        car,
      }))
      .filter((option) => option.value && !taken.has(option.value));
  }, [ownerCars, rows]);

  const ownerPlateMap = useMemo(() => {
    const map = new Map();
    ownerCars.forEach((car) => {
      const key = String(car.plate_no || '').trim().toUpperCase();
      if (key) map.set(key, car);
    });
    return map;
  }, [ownerCars]);

  const rowErrors = useMemo(() => {
    const output = {};
    rows.forEach((row) => {
      output[row.id] = buildRowErrors(row, invoicePeriod, invoicePeriodLabel, rows);
    });
    return output;
  }, [rows, invoicePeriod, invoicePeriodLabel]);

  const hasValidationErrors = useMemo(
    () => Object.values(rowErrors).some((item) => Object.keys(item).length > 0),
    [rowErrors]
  );

  const totals = useMemo(() => {
    const activeRows = rows.filter((row) => row.include === true && String(row.plate_no || '').trim());
    return {
      count: activeRows.length,
      gross: activeRows.reduce((sum, row) => sum + toNumber(row.gross_amount), 0),
      discount: activeRows.reduce((sum, row) => sum + toNumber(row.discount_amount), 0),
      net: activeRows.reduce((sum, row) => sum + toNumber(row.prorated_amount), 0),
    };
  }, [rows]);

  useEffect(() => {
    if (!saveValidation || !saveValidationRef.current) {
      return;
    }

    saveValidationRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    saveValidationRef.current.focus({ preventScroll: true });
  }, [saveValidation]);

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        setLoading(true);
        setError('');

        const response = await axios.get(
          `${rootUrl}/api/owners/${encodeURIComponent(ownerCode)}/transactions/${encodeURIComponent(transId)}`,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );

        if (!active) return;

        if (!response.data?.success) {
          setError(response.data?.message || 'Monthly invoice not found');
          return;
        }

        const invoice = response.data.data;
        if (String(invoice?.trans_type || '').toUpperCase() !== 'MOI') {
          setError('This page only edits monthly owner invoices.');
          return;
        }

        const period = periodFromDate(invoice?.date);
        const invoiceDay = dateInput(invoice?.date);
        const detailLines = Array.isArray(invoice?.detail_lines) ? invoice.detail_lines : [];

        setTransaction(invoice);
        setInvoiceDate(invoiceDay);
        setNotes(String(invoice?.notes || ''));
        setSaveSuccess(null);
        setRows(
          detailLines.length
            ? detailLines.map((row, index) => normalizeRow(row, period, invoiceDay, index))
            : []
        );
      } catch (err) {
        if (active) setError(err.response?.data?.message || 'Failed to load monthly invoice');
      } finally {
        if (active) setLoading(false);
      }
    };

    if (ownerCode && transId) load();
    else {
      setLoading(false);
      setError('Owner code and transaction ID are required');
    }

    return () => {
      active = false;
    };
  }, [authToken, ownerCode, transId]);

  useEffect(() => {
    let active = true;

    const loadOwnerCars = async () => {
      if (!ownerCode) return;

      try {
        setOwnerCarsLoading(true);
        const response = await axios.get(`${rootUrl}/owners/${encodeURIComponent(ownerCode)}/cars`, {
          headers: { Authorization: `Bearer ${authToken}` }
        });

        if (!active) return;

        if (response.data?.success) {
          setOwnerCars(Array.isArray(response.data.data) ? response.data.data : []);
        } else {
          setOwnerCars([]);
        }
      } catch (err) {
        if (active) {
          setOwnerCars([]);
        }
      } finally {
        if (active) {
          setOwnerCarsLoading(false);
        }
      }
    };

    const loadDefaultMonthlyAmount = async () => {
      try {
        const response = await axios.get(`${rootUrl}/system/owner_monthly_invoice_amount`, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
        if (!active) return;
        const amount = Number(response.data?.value || 0);
        setDefaultMonthlyAmount(Number.isFinite(amount) ? amount : 0);
      } catch (err) {
        if (active) {
          setDefaultMonthlyAmount(0);
        }
      }
    };

    loadOwnerCars();
    loadDefaultMonthlyAmount();

    return () => {
      active = false;
    };
  }, [authToken, ownerCode]);

  const updateRow = (id, field, value) => {
    setRows((previous) => previous.map((row) => (row.id === id ? buildRowDraft(row, field, value, invoiceDaysInMonth) : row)));
  };

  const addRow = () => {
    setSelectedPlateOption(null);
    setAddDialogOpen(true);
  };

  const confirmAddRow = () => {
    const selectedPlate = String(selectedPlateOption?.value || '').trim().toUpperCase();
    if (!selectedPlate || ownerPlateMap.has(selectedPlate) === false) {
      return;
    }

    const car = ownerPlateMap.get(selectedPlate);
    setRows((previous) => [...previous, makeRowFromCar(car, invoicePeriod, invoiceDate, defaultMonthlyAmount)]);
    setAddDialogOpen(false);
    setSelectedPlateOption(null);
  };

  const save = async () => {
    console.log('[MonthlyInvoiceEditor] Save clicked', {
      ownerCode,
      transId,
      invoiceStatus,
      isEditable,
      invoiceDate,
      rowCount: rows.length
    });

    if (!isEditable) {
      setError('Only draft invoices can be edited.');
      setSaveValidation({
        title: 'Save blocked',
        issues: ['This invoice is not in draft status, so it cannot be edited.']
      });
      return;
    }

    if (!invoicePeriod) {
      setError('Unable to determine the invoice period for this invoice.');
      setSaveValidation({
        title: 'Save blocked',
        issues: ['The invoice period could not be determined from the transaction date.']
      });
      return;
    }

    if (!invoiceDate || periodFromDate(invoiceDate) !== invoicePeriod) {
      setError(`Invoice date must stay within ${invoicePeriodLabel}.`);
      setSaveValidation({
        title: 'Save blocked',
        issues: [`Invoice date must stay within ${invoicePeriodLabel}.`]
      });
      return;
    }

    if (hasValidationErrors) {
      const issues = Object.entries(rowErrors)
        .flatMap(([rowId, rowError]) => Object.entries(rowError || {}).map(([field, message]) => `Row ${rows.findIndex((row) => row.id === rowId) + 1} ${field}: ${message}`));
      setError('Fix the highlighted line item errors before saving.');
      setSaveValidation({
        title: 'Save blocked',
        issues: issues.length > 0 ? issues : ['There are line item validation errors to fix before saving.']
      });
      return;
    }

    const normalizedRows = rows
      .map((row) => {
        if (row.include !== true) {
          return null;
        }

        const grossAmount = Math.max(0, toNumber(row.gross_amount));
        const discountAmount = Math.max(0, Math.min(toNumber(row.discount_amount), grossAmount));
        const rawDays = String(row.days_counted ?? '').trim();
        const daysCounted = rawDays === '' ? invoiceDaysInMonth : Math.max(0, parseInt(rawDays, 10) || 0);
        const rowPeriod = row.invoice_period || row.detail_invoice_period || invoicePeriod;
        return {
          include: true,
          plate_no: String(row.plate_no || '').trim().toUpperCase(),
          allocation_date: dateInput(row.allocation_date) || invoiceDate,
          base_amount: toNumber(row.base_amount),
          gross_amount: grossAmount,
          discount_amount: discountAmount,
          prorated_amount: Math.max(0, grossAmount - discountAmount),
          days_counted: daysCounted,
          note: String(row.note || '').trim(),
          invoice_period: rowPeriod,
        };
      })
      .filter((row) => row && row.plate_no);

    if (!normalizedRows.length) {
      setError('At least one included line item is required.');
      setSaveValidation({
        title: 'Save blocked',
        issues: ['At least one included line item is required before saving.']
      });
      return;
    }

    setSaving(true);
    setError('');
    setSaveValidation(null);

    try {
      const response = await axios.put(
        `${rootUrl}/api/owners/${encodeURIComponent(ownerCode)}/transactions/${encodeURIComponent(transId)}/monthly-invoice`,
        {
          invoice_date: invoiceDate,
          notes,
          line_items: normalizedRows,
        },
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      if (!response.data?.success) {
        setError(response.data?.message || 'Failed to update invoice');
        return;
      }

      const savedData = response.data?.data || {};
      const savedDetailLines = Array.isArray(savedData.detail_lines) ? savedData.detail_lines : normalizedRows;
      const nextRows = savedDetailLines.map((row, index) => normalizeRow(row, invoicePeriod, savedData.invoice_date || invoiceDate, index));
      const mergedTransaction = {
        ...transaction,
        ...savedData,
        id: savedData.invoice_ref || transaction?.id || transId,
        trans_type: 'MOI',
        short_desc: 'Monthly Invoice',
        owner_code: transaction?.owner_code || ownerCode,
        owner_name: transaction?.owner_name,
        owner_email: transaction?.owner_email,
        date: savedData.invoice_date || invoiceDate,
        amount: savedData.amount ?? totals.net,
        gross_amount: savedData.gross_amount ?? totals.gross,
        discount_amount: savedData.discount_amount ?? totals.discount,
        detail_lines: nextRows,
        notes,
      };

      setRows(nextRows);
      setTransaction(mergedTransaction);
      setSaveSuccess({
        message: response.data?.message || 'Monthly invoice updated successfully',
        invoice_ref: savedData.invoice_ref || mergedTransaction.id,
        invoice_date: savedData.invoice_date || invoiceDate,
        amount: savedData.amount ?? totals.net,
        transaction: mergedTransaction,
      });
      navigate(`/owners/transactions/${encodeURIComponent(ownerCode)}/${encodeURIComponent(transId)}`, { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update invoice');
    } finally {
      setSaving(false);
    }
  };

  const transactionLabel = transaction?.owner_name || transaction?.owner_code || ownerCode || '-';
  const documentNo = String(transaction?.id || transId || '').split(':').pop() || transaction?.id || transId || '-';
  const previewTransaction = saveSuccess?.transaction || transaction;
  const lockedMessage = invoiceStatus === 'REVERSED'
    ? 'This invoice has been reversed and is read-only.'
    : !isEditable
      ? 'This invoice is locked. Only draft invoices can be edited here.'
      : 'You can edit line items and save changes back to the transaction.';

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100">
        <Spinner className="h-12 w-12" color="orange" />
      </div>
    );
  }

  if (error && !transaction) {
    return (
      <EmptyState
        title="Monthly Invoice Editor"
        message={error}
        onBack={() => navigate('/owners/invoices/monthly')}
      />
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 text-slate-800">
      <div className="mx-auto max-w-[1460px] px-4 py-5 lg:px-6 lg:py-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => navigate(`/owners/transactions/${encodeURIComponent(ownerCode)}/${encodeURIComponent(transId)}`)}
              className="btn-back-dark"
            >
              <FaArrowLeft />
              Back to Transaction
            </Button>
            <Button
              onClick={() => navigate('/owners/invoices/monthly')}
              variant="outlined"
              className="border-slate-300 text-slate-700"
            >
              Monthly Invoices
            </Button>
          </div>

          <div className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold ${statusMeta.badgeClass}`}>
            {invoiceStatus === 'REVERSED' ? <FaLock /> : <FaFileInvoiceDollar />}
            {statusMeta.label}
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        )}

        {saveValidation && (
          <div
            ref={saveValidationRef}
            tabIndex={-1}
            aria-live="polite"
            className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-900 outline-none shadow-sm"
          >
            <div className="font-semibold">{saveValidation.title}</div>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              {saveValidation.issues.map((issue) => (
                <li key={issue}>{issue}</li>
              ))}
            </ul>
          </div>
        )}

        {saveSuccess && (
          <div className="mb-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 shadow-sm">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Saved</div>
                <div className="mt-1 text-base font-semibold text-emerald-900">{saveSuccess.message}</div>
                <div className="mt-1 text-sm text-emerald-700">
                  Ref {String(saveSuccess.invoice_ref || '').split(':').pop() || '-'} · Date {displayDate(saveSuccess.invoice_date) || '-'} · {formatMoney(saveSuccess.amount)}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => setPreviewOpen(true)} className="bg-emerald-700 shadow-none">Preview / Print / Send</Button>
                <Button
                  onClick={() => navigate(`/owners/transactions/${encodeURIComponent(ownerCode)}/${encodeURIComponent(transId)}`)}
                  variant="outlined"
                  className="border-emerald-300 text-emerald-700"
                >
                  Back to Transaction
                </Button>
              </div>
            </div>
          </div>
        )}

        <Card className="overflow-visible shadow-lg">
          <CardBody className="relative z-0 p-0">
            <div className="relative z-10 border-b border-slate-200 bg-white px-5 py-5 sm:px-6">
              <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                <div>
                  <div className="inline-flex items-center gap-2 rounded-full bg-orange-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-orange-700">
                    <FaFileInvoiceDollar />
                    Monthly Invoice Editor
                  </div>
                  <Typography variant="h4" className="mt-3 text-slate-900">{transactionLabel}</Typography>
                  <Typography className="mt-2 text-sm text-slate-600">
                    Document <span className="font-mono font-semibold">{documentNo}</span> for invoice period <span className="font-semibold">{invoicePeriodLabel}</span>
                  </Typography>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 xl:max-w-md">
                  {lockedMessage}
                </div>
              </div>
            </div>

            <div className="space-y-4 bg-slate-50 px-5 py-5 sm:px-6">
              <div className="grid gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <FaCalendarAlt />
                    Invoice Date
                  </div>
                  <Typography className="mt-2 text-sm text-slate-500">Must stay within {invoicePeriodLabel}.</Typography>
                  <DateInput
                    value={invoiceDate}
                    onChange={(e) => setInvoiceDate(e.target.value)}
                    min={dateBounds.min}
                    max={dateBounds.max}
                    disabled={!isEditable}
                    className="mt-4"
                  />
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <Typography variant="h6" className="text-slate-900">Invoice Notes</Typography>
                    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusMeta.badgeClass}`}>{statusMeta.label}</span>
                  </div>
                  <Typography className="text-sm text-slate-500">Invoice-level notes only. Keep line-specific details inside each row.</Typography>
                  <TextAreaInput
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    disabled={!isEditable}
                    className="mt-4 min-h-[120px]"
                  />
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm min-w-0">
                <div className="flex flex-col gap-3 border-b border-slate-200 px-4 py-4 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <Typography variant="h6" className="text-slate-900">Line Items</Typography>
                    <Typography className="text-sm text-slate-500">Only owner-owned plates can be added here.</Typography>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      onClick={addRow}
                      disabled={!isEditable}
                      className="inline-flex items-center gap-2 bg-orange-600 shadow-none disabled:opacity-60"
                    >
                      <FaPlus />
                      Add Plate
                    </Button>
                  </div>
                </div>

                <div className="hidden overflow-x-auto xl:block">
                  <div className="min-w-[1100px]">
                    <div className="grid grid-cols-[80px_140px_160px_120px_100px_160px_120px_160px] gap-3 bg-slate-100 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600">
                      <div>Include</div>
                      <div>Plate No</div>
                      <div>Allocation Date</div>
                      <div className="text-center">Base Amount</div>
                      <div className="text-center">Days Counted</div>
                      <div className="text-right">Gross Amount</div>
                      <div className="text-center">Discount</div>
                      <div className="text-right">Net Amount</div>
                    </div>

                    <div>
                      {rows.length > 0 ? (
                        rows.map((row, index) => (
                          <DesktopRow
                            key={row.id}
                            row={row}
                            index={index}
                            errors={rowErrors[row.id] || {}}
                            isEditable={isEditable}
                            onChange={updateRow}
                          />
                        ))
                      ) : (
                        <div className="px-4 py-10 text-center text-sm text-slate-500">
                          No line items yet. Use <span className="font-semibold text-orange-600">Add Plate</span> to add a plate owned by this owner.
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-3 p-4 xl:hidden">
                  {rows.length > 0 ? (
                    rows.map((row, index) => (
                      <MobileRow
                        key={row.id}
                        row={row}
                        index={index}
                        errors={rowErrors[row.id] || {}}
                        isEditable={isEditable}
                        onChange={updateRow}
                      />
                    ))
                  ) : (
                    <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                      No line items yet. Add a plate from this owner to begin.
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Line Count</div>
                    <div className="mt-1 text-2xl font-bold text-slate-900">{totals.count}</div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Gross Total</div>
                    <div className="mt-1 text-2xl font-bold text-slate-900">{formatMoney(totals.gross)}</div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Discount Total</div>
                    <div className="mt-1 text-2xl font-bold text-slate-900">{formatMoney(totals.discount)}</div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Net Total</div>
                    <div className="mt-1 text-2xl font-bold text-emerald-700">{formatMoney(totals.net)}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 z-20 flex flex-col gap-3 border-t border-slate-200 bg-white px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
              <div className="text-sm text-slate-500">
                {hasValidationErrors ? 'There are line item errors to fix before saving.' : 'All visible fields are ready to save.'}
              </div>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Button
                  onClick={() => navigate(`/owners/transactions/${encodeURIComponent(ownerCode)}/${encodeURIComponent(transId)}`)}
                  variant="outlined"
                  className="border-slate-300 text-slate-700"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={save}
                  disabled={saving || !isEditable}
                  className="inline-flex items-center justify-center gap-2 bg-orange-600 shadow-none disabled:opacity-60"
                >
                  <FaSave />
                  {saving ? 'Saving...' : 'Save Invoice'}
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>

      <Dialog open={addDialogOpen} handler={setAddDialogOpen} size="sm">
        <DialogHeader>Add Owner Plate</DialogHeader>
        <DialogBody className="space-y-4">
          <Typography className="text-sm text-slate-600">
            Select a plate that belongs to this owner. Already added plates are hidden from the list.
          </Typography>

          <div>
            <ReactSelect
              options={ownerPlateOptions}
              value={selectedPlateOption}
              onChange={setSelectedPlateOption}
              isSearchable
              isClearable
              isDisabled={ownerCarsLoading || ownerPlateOptions.length === 0}
              placeholder={ownerCarsLoading ? 'Loading owner plates...' : 'Choose plate number'}
              classNamePrefix="react-select"
            />
            {!ownerCarsLoading && ownerPlateOptions.length === 0 && (
              <div className="mt-2 text-sm text-slate-500">No additional owner plates are available to add.</div>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="text" onClick={() => setAddDialogOpen(false)} className="mr-2">
            Cancel
          </Button>
          <Button
            onClick={confirmAddRow}
            disabled={!selectedPlateOption || ownerCarsLoading}
            className="bg-orange-600 shadow-none disabled:opacity-60"
          >
            Add Plate
          </Button>
        </DialogFooter>
      </Dialog>

      <OwnerTransactionPreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        transaction={previewTransaction}
      />
    </div>
  );
};

export default MonthlyInvoiceEditor;
